import { Card, CardContent, FormField, Input, MetricCard, PageHeader } from "@jod/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useAnalyticsSummary } from "../../features/backoffice/queries.ts";
import { formatMoney } from "../../lib/format.ts";

export const Route = createFileRoute("/_app/dashboard")({ component: DashboardScreen });

function toRfc3339StartOfDay(dateInput: string): string {
  return new Date(`${dateInput}T00:00:00.000Z`).toISOString();
}

function toRfc3339EndOfDay(dateInput: string): string {
  return new Date(`${dateInput}T23:59:59.999Z`).toISOString();
}

function DashboardScreen() {
  // FR-OWN-150: date-range selector. Both fields empty = server default
  // (current/most-recently-opened session).
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const { data: s } = useAnalyticsSummary({
    from: fromDate ? toRfc3339StartOfDay(fromDate) : undefined,
    to: toDate ? toRfc3339EndOfDay(toDate) : undefined,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard / Analytics"
        subtitle="Aggregated AEV-* data only; no raw PII."
      />
      <Card>
        <CardContent className="flex flex-wrap items-end gap-4 p-5">
          <FormField id="dashboard-from-date" label="From">
            <Input
              id="dashboard-from-date"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
            />
          </FormField>
          <FormField id="dashboard-to-date" label="To">
            <Input
              id="dashboard-to-date"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
            />
          </FormField>
          {s?.rangeFrom && s?.rangeTo ? (
            <p className="text-muted-foreground text-xs">
              Showing {new Date(s.rangeFrom).toLocaleString()} –{" "}
              {new Date(s.rangeTo).toLocaleString()}
            </p>
          ) : null}
        </CardContent>
      </Card>
      {s ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
            <MetricCard label="Active tables" value={s.activeTables} />
            <MetricCard label="Orders tonight" value={s.ordersTonight} />
            <MetricCard label="Gifts sent" value={s.giftsSent} />
            <MetricCard
              label="Gift accepted rate"
              value={`${Math.round(s.giftAcceptedRate * 100)}%`}
            />
            <MetricCard label="Estimated revenue" value={formatMoney(s.estimatedRevenueMinor)} />
            <MetricCard
              label="Reports"
              value={s.reportsCount}
              tone={s.reportsCount ? "warning" : "neutral"}
            />
          </div>
          <Card>
            <CardContent className="space-y-4 p-5">
              <h2 className="font-display font-semibold text-lg">Orders by hour</h2>
              <div className="flex h-44 items-end gap-4">
                {s.series.map((point) => (
                  <div key={point.label} className="flex flex-1 flex-col items-center gap-2">
                    <div
                      className="w-full rounded-t bg-primary"
                      style={{ height: `${Math.max(12, point.value * 2)}px` }}
                    />
                    <span className="text-muted-foreground text-xs">{point.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          {s.giftFunnel ? (
            <FunnelCard
              title="Gift funnel"
              stages={[
                { label: "Sent", value: s.giftFunnel.sent },
                { label: "Accepted", value: s.giftFunnel.accepted },
                { label: "Fulfilled", value: s.giftFunnel.fulfilled },
                { label: "Rejected", value: s.giftFunnel.rejected },
                { label: "Expired", value: s.giftFunnel.expired },
              ]}
            />
          ) : null}
          {s.orderFunnel ? (
            <FunnelCard
              title="Order funnel"
              stages={[
                { label: "Pending", value: s.orderFunnel.pending },
                { label: "Accepted", value: s.orderFunnel.accepted },
                { label: "Preparing", value: s.orderFunnel.preparing },
                { label: "Ready", value: s.orderFunnel.ready },
                { label: "Served", value: s.orderFunnel.served },
                { label: "Cancelled", value: s.orderFunnel.cancelled },
                { label: "Rejected", value: s.orderFunnel.rejected },
              ]}
            />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function FunnelCard({
  title,
  stages,
}: {
  title: string;
  stages: { label: string; value: number }[];
}) {
  const max = Math.max(1, ...stages.map((stage) => stage.value));
  return (
    <Card>
      <CardContent className="space-y-3 p-5">
        <h2 className="font-display font-semibold text-lg">{title}</h2>
        <div className="space-y-2">
          {stages.map((stage) => (
            <div key={stage.label} className="flex items-center gap-3">
              <span className="w-24 shrink-0 text-muted-foreground text-xs">{stage.label}</span>
              <div className="h-3 flex-1 overflow-hidden rounded-full bg-muted/40">
                <div
                  className="h-full rounded-full bg-primary"
                  style={{ width: `${Math.max(2, (stage.value / max) * 100)}%` }}
                />
              </div>
              <span className="w-10 shrink-0 text-right text-xs">{stage.value}</span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
